import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ReactComponent as UploadIcon } from 'assets/icons/cloud.svg';
import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import Button from 'components/Button/Button';
import Modal from 'components/Modal/Modal';
import QuestionErrorModal from 'components/QuestionErrorModal/QuestionErrorModal';
import { DATA_SETS_MIME_TYPES_ACCEPT, MAX_LOGO_SIZE } from 'constants/general';
import { BUTTON_VARIANT, PRESIGNED_FILE_TYPES } from 'constants/interfaces';
import useFileStorageUpload from 'hooks/useFileStorageUpload';
import useImportErrorModal from 'hooks/useImportErrorModal';
import {
  useImportAgentSessionBomXlsxMutation,
  useLazyGetAgentSessionBomXlsxExampleQuery,
} from 'store/slices/valueChainMap/apis/valueChainMapApi';

import type IQuestionErrorList from 'components/QuestionErrorModal/interfaces/IQuestionErrorList';
import type ISourceType from 'components/QuestionErrorModal/interfaces/ISourceType';
import type { TPresignedFileTypes } from 'constants/interfaces';
import type IAICreatorBoMUploadModal from './interfaces/IAICreatorBoMUploadModal';

import styles from './scss/AICreatorBoMUploadModal.module.scss';

const isRecord = (value: unknown): value is Record<string, unknown> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
);

const normalizeImportErrorSource = (source: unknown): ISourceType | null => {
  if (!isRecord(source) || typeof source.column !== 'string' || typeof source.error !== 'string') {
    return null;
  }

  const params = isRecord(source.params) ? source.params : {};
  const values = typeof params.values === 'string' ? params.values : undefined;

  return {
    column: source.column,
    error: source.error,
    errorCode: typeof source.errorCode === 'string' ? source.errorCode : '',
    params: {
      value: typeof params.value === 'string' ? params.value : '',
      ...(values ? { values } : {}),
    },
  };
};

const getImportRowErrors = (error: unknown): IQuestionErrorList | undefined => {
  if (!isRecord(error) || !isRecord(error.data)) {
    return undefined;
  }

  const { message } = error.data;

  if (!isRecord(message) || !isRecord(message.rowErrors)) {
    return undefined;
  }

  const questionErrorList: IQuestionErrorList = {};

  Object.entries(message.rowErrors).forEach(([rowKey, rowValue]) => {
    if (!isRecord(rowValue)) {
      return;
    }

    const rowErrors: Record<string, ISourceType[]> = {};

    Object.entries(rowValue).forEach(([columnKey, columnValue]) => {
      if (!Array.isArray(columnValue)) {
        return;
      }

      const columnErrors = columnValue
        .map(normalizeImportErrorSource)
        .filter((item): item is ISourceType => Boolean(item));

      if (columnErrors.length > 0) {
        rowErrors[columnKey] = columnErrors;
      }
    });

    if (Object.keys(rowErrors).length > 0) {
      questionErrorList[rowKey] = rowErrors;
    }
  });

  return Object.keys(questionErrorList).length > 0 ? questionErrorList : undefined;
};

function AICreatorBoMUploadModal({
  open,
  valueChainMapId,
  sessionId,
  bomId,
  onClose,
  onImported,
}: IAICreatorBoMUploadModal) {
  const { t } = useTranslation();
  const [dataFile, setDataFile] = useState<File | null>(null);
  const [dropzoneError, setDropzoneError] = useState('');
  const [questionErrorList, setQuestionErrorList] = useState<IQuestionErrorList>();
  const { uploadToStorage, isLoading: isPresignedPostLoading } = useFileStorageUpload();
  const [downloadExample, { isFetching: isDownloadExampleLoading }] = useLazyGetAgentSessionBomXlsxExampleQuery();
  const [importAgentSessionBomXlsx, { isLoading: isImportLoading }] = useImportAgentSessionBomXlsxMutation();
  const {
    isImportErrorModalVisible,
    importErrorMessage,
    handleImportError,
    handleCloseImportErrorModal,
  } = useImportErrorModal();

  const isBusy = isPresignedPostLoading || isImportLoading;
  const isUploadDisabled = isBusy || !valueChainMapId || !sessionId || !bomId;

  const resetUploadState = useCallback(() => {
    setDataFile(null);
    setDropzoneError('');
  }, []);

  const handleCloseQuestionErrorModal = useCallback(() => {
    setQuestionErrorList(undefined);
    handleCloseImportErrorModal();
  }, [handleCloseImportErrorModal]);

  const uploadFile = useCallback((file: File | string, fileType: TPresignedFileTypes) => {
    if (typeof file === 'object') {
      return uploadToStorage(file, fileType)
        .then((presignedData) => presignedData.fields.Url);
    }

    return Promise.resolve(file);
  }, [uploadToStorage]);

  const handleDownloadExample = useCallback(() => {
    if (!valueChainMapId || !sessionId || !bomId) {
      return;
    }

    downloadExample({
      valueChainMapId,
      sessionId,
      bomId,
    });
  }, [bomId, downloadExample, sessionId, valueChainMapId]);

  const handleClose = useCallback(() => {
    if (isBusy) {
      return;
    }

    resetUploadState();
    handleCloseQuestionErrorModal();
    onClose();
  }, [handleCloseQuestionErrorModal, isBusy, onClose, resetUploadState]);

  useEffect(() => {
    if (!open) {
      resetUploadState();
      handleCloseQuestionErrorModal();
    }
  }, [handleCloseQuestionErrorModal, open, resetUploadState]);

  const handleImportFile = useCallback((file: File) => {
    if (!valueChainMapId || !sessionId || !bomId) {
      return;
    }

    setDropzoneError('');
    setDataFile(file);

    uploadFile(file, PRESIGNED_FILE_TYPES.valueChainAttachment)
      .then((url) => importAgentSessionBomXlsx({
        valueChainMapId,
        sessionId,
        bomId,
        url,
      }).unwrap())
      .then(() => {
        toast.success(t('notifications.valueChainBomUploadSuccess'));
        resetUploadState();
        onImported?.();
        onClose();
      })
      .catch((error: unknown) => {
        setQuestionErrorList(getImportRowErrors(error));
        handleImportError(error);
        resetUploadState();
      });
  }, [
    bomId,
    handleImportError,
    importAgentSessionBomXlsx,
    onClose,
    onImported,
    resetUploadState,
    sessionId,
    t,
    uploadFile,
    valueChainMapId,
  ]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const [file] = acceptedFiles;

    if (!file) {
      return;
    }

    handleImportFile(file);
  }, [handleImportFile]);

  const onDropRejected = useCallback((fileRejections: { errors: { message: string }[] }[]) => {
    setDataFile(null);
    setDropzoneError(fileRejections[0]?.errors.map((item) => item.message).join(', ') || '');
  }, []);

  const {
    getRootProps,
    getInputProps,
    open: openFilePicker,
    isDragActive,
  } = useDropzone({
    accept: DATA_SETS_MIME_TYPES_ACCEPT,
    maxSize: MAX_LOGO_SIZE,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    disabled: isUploadDisabled,
    onDrop,
    onDropRejected,
  });

  const rootProps = getRootProps();

  const dropZoneClassName = useMemo(() => [
    styles.dropZone,
    isDragActive ? styles.dropZoneDragActive : '',
    dropzoneError ? styles.dropZoneError : '',
  ].filter(Boolean).join(' '), [dropzoneError, isDragActive]);

  return (
    <>
      <Modal
        open={open}
        title={t('valueChainMap.bomUnitsUploadModalTitle')}
        className={styles.modal}
        titleClassName={styles.titleWrapper}
        titleAction={(
          <Button
            title={t('dataSetsManagement.downloadExample')}
            startIcon={<DownloadIcon />}
            variant={BUTTON_VARIANT.ghost}
            className={styles.downloadButton}
            onClick={handleDownloadExample}
            isLoading={isDownloadExampleLoading}
            disabled={isBusy || !sessionId || !bomId}
          />
        )}
        showLoader={isBusy}
        isButtonsVisible={false}
      >
        <div className={styles.content}>
          <div
            className={dropZoneClassName}
            onDragEnter={rootProps.onDragEnter}
            onDragOver={rootProps.onDragOver}
            onDragLeave={rootProps.onDragLeave}
            onDrop={rootProps.onDrop}
            ref={rootProps.ref}
            tabIndex={rootProps.tabIndex}
          >
            <input {...getInputProps()} />
            <div className={styles.dropZoneInner}>
              <div className={styles.dropZoneIcon}>
                <UploadIcon />
              </div>
              <div className={styles.dropZoneTitle}>
                {t('valueChainMap.bomUnitsUploadDropzoneTitle')}
              </div>
              <div className={styles.dropZoneSubtitle}>
                {t('valueChainMap.bomUnitsUploadDropzoneSubtitle')}
              </div>
              <Button
                title={t('common.browse')}
                variant={BUTTON_VARIANT.primary}
                className={styles.browseButton}
                onClick={openFilePicker}
                disabled={isUploadDisabled}
              />
              {dataFile ? (
                <div className={styles.fileName}>
                  {dataFile.name}
                </div>
              ) : null}
            </div>
          </div>
          {dropzoneError ? <div className={styles.error}>{dropzoneError}</div> : null}
          <div className={styles.footer}>
            <Button
              title={t('common.cancel')}
              variant={BUTTON_VARIANT.cancel}
              className={styles.cancelButton}
              onClick={handleClose}
              disabled={isBusy}
            />
          </div>
        </div>
      </Modal>
      <QuestionErrorModal
        isOpen={isImportErrorModalVisible}
        onClose={handleCloseQuestionErrorModal}
        title={t('valueChainMap.bomUnitsModalTitle')}
        questionErrorList={questionErrorList}
        errorLog={questionErrorList ? '' : importErrorMessage}
        hideRecommendations
      />
    </>
  );
}

export default AICreatorBoMUploadModal;
